import { Box } from '@mui/material';
import Divider from '@mui/material/Divider';
import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { Types } from '../../../enums/TypeEnums';
import {
  useContactInterestsQuery,
  useGetInterestGroupsQuery,
  useUpdateContactInterestsMutation
} from '../../../generated/graphql';
import { canUserEdit } from '../../../utils/rightUtils';
import { Colors } from '../../../utils/theme';
import CancelButton from '../../Common/Buttons/CancelButton';
import EditButton from '../../Common/Buttons/EditButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import CheckboxComponent from '../../Common/Fields/CheckboxComponent';

interface IProps {
  id: string;
}

const ContactInterestsTab: React.FunctionComponent<IProps> = ({ id }) => {
  const { state, dispatch } = useContext(AppContext);
  const { data } = useContactInterestsQuery({
    variables: { contact_id: parseInt(id) },
    fetchPolicy: 'no-cache'
  });
  const [initialContactInterestIds, setInitialContactInterestIds] = useState<string[]>([]);
  const [contactInterestIds, setContactInterestIds] = useState<string[]>([]);
  const interestGroups = useGetInterestGroupsQuery();
  const [updateContactInterests, updateContactInterestsResult] =
    useUpdateContactInterestsMutation();
  const [editable, setEditable] = useState(false);
  const canEdit = canUserEdit(state.user, Types.Contact);

  useEffect(() => {
    if (data && data.result && data.result.length > 0) {
      setContactInterestIds(data.result.map((i) => i?.interest_id as string));
      setInitialContactInterestIds(data.result.map((i) => i?.interest_id as string));
    }
  }, [data]);

  const handleEditClicked = () => {
    setEditable(true);
  };

  const handleCheckClicked = (interest_id: string) => {
    if (contactInterestIds.includes(interest_id)) {
      setContactInterestIds(contactInterestIds.filter((id) => id !== interest_id));
    } else {
      setContactInterestIds([interest_id, ...contactInterestIds]);
    }
  };

  const handleCancelClicked = () => {
    setEditable(false);
    setContactInterestIds(initialContactInterestIds);
  };

  const handleSaveClicked = () => {
    setEditable(false);
    updateContactInterests({
      variables: {
        id: parseInt(id),
        interest_ids: contactInterestIds.map((i) => parseInt(i))
      },
      update(_, { data }) {
        if (data?.result) {
          setContactInterestIds(data.result as string[]);
          setInitialContactInterestIds(data.result as string[]);

          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `You have successfully updated ${state.selectedContact.name}'s interests`
            }
          });
        }
      }
    }).catch((e) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message,
          autoHide: 6000
        }
      })
    );
  };

  return (
    <Box sx={{ padding: '80px', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
        <EditButton
          id="contact-interest-edit-button"
          onClick={handleEditClicked}
          disabled={!canEdit || editable}
          editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      <Box sx={{ display: 'flex', flexFlow: 'column', gap: '80px' }}>
        {interestGroups.data?.result.map((ig, index) => (
          <Box key={index}>
            <Box
              id={`interest-group-${ig?.name.replaceAll(' ', '-')}`}
              sx={{
                fontSize: '14px',
                fontWeight: 'bold',
                color: editable ? Colors.main : Colors.gray
              }}
            >
              {ig?.name.toUpperCase()}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0 60px' }}>
              {ig?.interests.map((i, index2) => (
                <CheckboxComponent
                  key={index2}
                  id={i?.id || ''}
                  label={i?.name || ''}
                  value={i ? contactInterestIds.includes(i.id) : false}
                  onChange={() => handleCheckClicked(i?.id as string)}
                  disabled={!editable}
                />
              ))}
            </Box>
            {interestGroups.data && interestGroups.data?.result.length - 1 > index && (
              <Divider sx={{ background: '#69AAF34A', marginTop: '80px' }} />
            )}
          </Box>
        ))}
      </Box>
      {editable && (
        <Box className="centralize" sx={{ marginTop: '160px' }}>
          <CancelButton
            id="contact-interests-cancel-button"
            disabled={!editable || updateContactInterestsResult.loading}
            onClick={handleCancelClicked}
            text="Cancel"
          />
          <SaveButton
            id="contact-interests-save-button"
            disabled={!editable || updateContactInterestsResult.loading}
            onClick={handleSaveClicked}
            text="Save"
          />
        </Box>
      )}
    </Box>
  );
};

export default ContactInterestsTab;
